import { useState } from 'react';
import { useToast } from '@/hooks/use-toast';
import { CheckCircle, ArrowRight, Star, Sparkles, Users, Lightbulb, Target, Sparkles as SparklesIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import Header from '@/components/Header';
import SectionDivider from '@/components/SectionDivider';
import Footer from '@/components/Footer';
import { submitEmailToSupabase } from '@/lib/supabase';
import { trackEmailFocus, trackEmailEntered, trackCTAClick, trackEmailSubmissionSuccess, trackEmailSubmissionError } from '@/lib/analytics';

const DesignPartner = () => {
  const [formData, setFormData] = useState({
    email: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const formSource = 'design_partner';

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const newEmail = e.target.value;
    setFormData({
      ...formData,
      [e.target.name]: newEmail
    });
    
    // Track email entered for design partner form
    if (e.target.name === 'email') {
      trackEmailEntered(formSource, newEmail);
    }
  };

  const handleEmailFocus = () => {
    trackEmailFocus(formSource);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.email.trim()) return;

    // Track CTA click
    trackCTAClick(formSource, formData.email);

    setIsSubmitting(true);
    
    try {
      const result = await submitEmailToSupabase(formData.email, 'design_partner');
      
      if (result.success) {
        // Track successful submission
        trackEmailSubmissionSuccess(formSource, formData.email);
        
        toast({
          title: "Thanks for your interest!",
          description: "We'll be in touch soon with more details about Carto.",
        });
        setFormData({
          email: ''
        });
      } else {
        // Track failed submission
        trackEmailSubmissionError(formSource, formData.email, result.error || 'Unknown error');
        
        toast({
          title: "Oops! Something went wrong",
          description: result.error || "Please try again later.",
          variant: "destructive",
        });
      }
    } catch (error) {
      // Track failed submission
      trackEmailSubmissionError(formSource, formData.email, error instanceof Error ? error.message : 'Network error');
      
      toast({
        title: "Oops! Something went wrong",
        description: "Please try again later.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const requirements = [
    "Connect at least one data source to the Carto platform",
    "Provide honest, ongoing feedback throughout the program",
    "Complete a short wrap-up survey at the end"
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-20 left-10 text-blue-100 opacity-20 animate-pulse">
          <Users size={120} />
        </div>
        <div className="absolute top-40 right-20 text-indigo-200 opacity-15 animate-bounce">
          <Lightbulb size={80} />
        </div>
        <div className="absolute bottom-40 left-20 text-slate-200 opacity-20">
          <Target size={100} />
        </div>
        <div className="absolute top-1/2 right-10 text-blue-100 opacity-10">
          <CheckCircle size={150} />
        </div>
        <div className="absolute top-32 left-1/3 text-yellow-200 opacity-30 animate-pulse">
          <Star size={24} />
        </div>
        <div className="absolute bottom-60 right-1/4 text-blue-200 opacity-25 animate-bounce">
          <SparklesIcon size={32} />
        </div>
        <div className="absolute top-2/3 left-1/4 text-indigo-200 opacity-20">
          <ArrowRight size={28} />
        </div>
        
        {/* Floating particles */}
        <div className="absolute top-1/4 right-1/3 w-2 h-2 bg-blue-300 rounded-full opacity-40 animate-ping"></div>
        <div className="absolute top-3/4 left-1/5 w-3 h-3 bg-indigo-400 rounded-full opacity-30 animate-pulse"></div>
        <div className="absolute top-1/2 right-1/5 w-1 h-1 bg-yellow-400 rounded-full opacity-50 animate-bounce"></div>
      </div>

      <Header />
      
      {/* Hero Section with Product Description */}
      <section className="container mx-auto px-4 pt-16 pb-12 relative z-10">
        <div className="text-center mb-16">
          <Badge variant="outline" className="mb-6 text-blue-600 border-blue-200 bg-blue-50 animate-pulse">
            🤝 Design Partnership Program
          </Badge>
          <h1 className="text-4xl md:text-5xl font-bold mb-8 bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 bg-clip-text text-transparent">
            Get Early Access to Carto
          </h1>
        </div>

        {/* Main Content - Product Description + Phone */}
        <div className="max-w-5xl mx-auto px-8">
          <div className="grid lg:grid-cols-2 gap-2 items-center">
            {/* Left side - Product Description */}
            <div className="space-y-6 max-w-lg pr-4">
              <div className="space-y-6 text-lg leading-relaxed text-slate-700">
                <p>
                  <strong className="text-slate-800">Carto is an AI COO designed specifically for small businesses.</strong> Think of it as your financial partner—helping you understand the numbers, map out a plan to achieve your goals, and take tedious admin tasks off your plate so you can focus on growth.
                </p>
                
                <p>
                  It's not another tool to learn—<strong className="text-slate-800">everything happens through simple text messages.</strong> Chat with Carto just like you would a trusted co-worker. Behind the scenes, Carto seamlessly integrates with platforms you already use, like QuickBooks and Square, as well as powerful new AI tools you don't have time to explore. So it always has the full context of your business at its fingertips.
                </p>
              </div>
              
              <div className="pt-4">
                <Button 
                  onClick={() => window.location.href = '/'}
                  variant="outline"
                  className="border-blue-200 text-blue-600 hover:bg-blue-50 hover:border-blue-300 transition-all duration-300"
                >
                  Learn More
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Right side - Phone Mockup */}
            <div className="flex justify-center pl-4">
              {/* iPhone-style messaging interface */}
              <div className="bg-gradient-to-br from-gray-900 to-black rounded-[3rem] p-4 max-w-sm shadow-2xl hover:shadow-3xl transition-all duration-500 transform hover:scale-105">
                {/* iPhone header */}
                <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-t-[2.5rem] px-6 py-4 text-white">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-sm font-medium">9:41</div>
                    <div className="w-20 h-6 bg-black rounded-full relative">
                      <div className="absolute inset-1 bg-gray-800 rounded-full"></div>
                    </div>
                    <div className="flex gap-1 text-sm">
                      <span className="animate-pulse">📶</span>
                      <span>🔋</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-full flex items-center justify-center shadow-lg">
                      <span className="text-white font-bold text-sm">C</span>
                    </div>
                    <div>
                      <div className="font-semibold">Carto</div>
                      <div className="text-xs text-green-400 flex items-center gap-1">
                        <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                        AI
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Messages */}
                <div className="bg-gradient-to-br from-gray-900 to-black rounded-b-[2.5rem] px-4 py-6 space-y-4 min-h-[400px]">
                  <div className="flex justify-start animate-fade-in">
                    <div className="bg-gradient-to-r from-gray-700 to-gray-600 text-white rounded-2xl rounded-bl-md px-4 py-3 max-w-xs text-left shadow-lg text-sm">
                      Revenue is up 15% vs last month! 📈 Your biggest win was the Johnson project. Cash flow looks healthy, but I noticed 3 overdue invoices worth $2,400. Want me to send follow-ups?
                    </div>
                  </div>
                  
                  <div className="flex justify-end animate-fade-in" style={{animationDelay: '0.5s'}}>
                    <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-2xl rounded-br-md px-4 py-3 max-w-xs text-left shadow-lg">
                      Yes, send those follow-ups.
                    </div>
                  </div>
                  
                  <div className="flex justify-start animate-fade-in" style={{animationDelay: '1s'}}>
                    <div className="bg-gradient-to-r from-gray-700 to-gray-600 text-white rounded-2xl rounded-bl-md px-4 py-3 max-w-xs text-left shadow-lg">
                      Done! ✅ Sent personalized follow-ups to all 3 clients. I'll track responses and update you.
                    </div>
                  </div>
                  
                  <div className="flex justify-start animate-fade-in" style={{animationDelay: '1.5s'}}>
                    <div className="bg-gradient-to-r from-gray-600 to-gray-500 text-gray-300 rounded-2xl rounded-bl-md px-4 py-2 text-xs shadow-lg">
                      Your AI COO
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <SectionDivider />

      {/* Design Partnership Section */}
      <section className="container mx-auto px-4 py-16 relative z-10">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-6">
              Design Partnership Program
            </h2>
            <div className="flex items-center justify-center gap-4 text-sm text-slate-500 mb-6">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                <span>Limited spaces available</span>
              </div>
            </div>
            <p className="text-lg text-slate-600 leading-relaxed max-w-3xl mx-auto mb-4">
              We're looking for small business owners to help us refine Carto through real-world testing and feedback. 
              As a Design Partner, you'll get early access to Carto while helping us build the perfect AI COO for businesses like yours.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8 mb-8">
            {/* Left side - Benefits */}
            <Card className="border-0 shadow-lg bg-white/70 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="text-2xl text-slate-800">
                  What you get
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-700 text-lg">Early access to Carto</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-700 text-lg">Weekly 1:1 check-ins with our leadership team</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-700 text-lg">Exclusive educational sessions and expert support</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Right side - Asks */}
            <Card className="border-0 shadow-lg bg-white/70 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="text-2xl text-slate-800">
                  Asks in return
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-4">
                  {requirements.map((requirement, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                      <span className="text-slate-700 text-lg">{requirement}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <Footer 
        email={formData.email} 
        setEmail={(email) => setFormData({...formData, email})} 
        handleSubmit={handleSubmit} 
        isSubmitting={isSubmitting}
      />
    </div>
  );
};

export default DesignPartner; 